import { Modal } from "@circle-react-shared/uikit/ModalV2";
import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { ReportsList } from "@circle-react/components/SettingsApp/Moderation/ReportFlaggedContentModal/ReportsList";
import { VisibilityInfo } from "@circle-react/components/SettingsApp/Moderation/ReportFlaggedContentModal/VisibilityInfo";

import { t } from "@/i18n-js/instance";
import {
  CHAT_ROOM_MESSAGE_TYPE,
  type FlaggedContentableType,
} from "@/react/types/FlaggedContent";

import { FlaggedContentActions } from "../../shared/Moderation/FlaggedContentActions";
import { useApproveRejectContent } from "../useApproveRejectContent";
import type { ModerationActionOptions } from "../useModerationApi";

const contentPortalId = "review-modal-content";
const i18nRoot = "settings.moderation.review_flagged_content_modal";

interface ModerationInfoModalUIKitComponentProps {
  flaggedContentId: string;
  flaggedContentType: FlaggedContentableType;
}

export const ModerationInfoModalUIKitComponent = ({
  flaggedContentId,
  flaggedContentType,
}: ModerationInfoModalUIKitComponentProps) => {
  const modal = useModal();

  const {
    approveFlaggedContent,
    rejectFlaggedContent,
    flaggedContent,
    isApproving,
    isRejecting,
  } = useApproveRejectContent(flaggedContentId, flaggedContentType);

  const {
    author_is_flagged: isAuthorFlagged,
    flagged_contentable,
    initiator_is_flagged_for_connect: isFlaggedForConnect,
    reports = [],
    status,
  } = flaggedContent ?? {};
  const type = flagged_contentable?.type;
  const reportsCount = reports.length;

  const onApprove = async (options?: ModerationActionOptions) => {
    approveFlaggedContent({ options });
    await modal.hide();
  };

  const onReject = async (
    reason: string | null,
    options?: ModerationActionOptions,
  ) => {
    rejectFlaggedContent({ reason, options });
    await modal.hide();
  };

  if (status === undefined || !type) {
    return null;
  }

  return (
    <Modal
      isOpen={modal.visible}
      onClose={modal.hide}
      contentPortalId={contentPortalId}
    >
      <Modal.Overlay />
      <Modal.Content size="lg">
        <Modal.Header>
          <Modal.Title size="sm">
            {t("flagged_contents.moderation_info.reports_title", {
              count: reportsCount,
            })}
          </Modal.Title>
          <Modal.HeaderActions className="absolute right-0 top-0 mr-6 mt-6">
            <Modal.CloseButton onClick={modal.hide} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="flex flex-col !p-0">
          <VisibilityInfo content={flaggedContent} />
          <ReportsList reports={reports} />
        </Modal.Body>
        <Modal.Footer className="!p-0">
          <FlaggedContentActions
            i18nRoot={i18nRoot}
            status={status}
            isApproving={isApproving}
            isRejecting={isRejecting}
            isAuthorFlagged={isAuthorFlagged}
            isEmbeddedChat={
              type === CHAT_ROOM_MESSAGE_TYPE &&
              Boolean(flagged_contentable?.is_embedded)
            }
            isFlaggedForConnect={isFlaggedForConnect}
            onApprove={onApprove}
            onReject={onReject}
            type={type}
          />
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
